import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ccmApi } from '../lib/api';
import { groupByPatient } from './note-expander-flow';
import { NoteExpanderV2Prefs } from './note-expander-v2-prefs';
import { NoteExpanderV2Review } from './note-expander-v2-review';
import { NoteExpanderV2Feedback } from './note-expander-v2-feedback';
import { NoteExpanderV2Playground } from './note-expander-v2-playground';
import { NoteExpanderV2Style } from './note-expander-v2-style';
import {
  mergeInbox, chunk, isTerminal, rowState, nextPollDelay, POLL_BUDGET_MS, copyFor,
  modeLabel, modelOptionLabel, prefsBody, fellBackSeen, markFellBackSeen, newClientRequestId,
  subTabFromSearch,
} from '../lib/note-expander-v2';
import { bannerShows, dismissBanner, updateNotice, markUpdateSeen } from '../lib/note-expander-v2-style';
import '../styles/note-expander-v2.css';

// Note Expander v2, the web page (PR 5-UI-1, O191; DESIGN §7.1). Rendered by
// note-expander-entry.jsx only when the practice's engine switch is v2. Two tabs:
//   Inbox        v1's unsigned-draft list (GET /api/note-expander/inbox), each draft joined to its
//                latest v2 run (GET /v2/runs/latest): a mode switch per note (the default from
//                Preferences), Expand, and the run's state in words. A run is polled through the
//                same status row until it settles, and keeps polling while Preferences is open.
//                A ready run opens the review (note-expander-v2-review.jsx, 5-UI-2); closing it
//                refreshes that draft's row.
//   Playground   note-expander-v2-playground.jsx (PR 5b): models side by side on one draft;
//                mounted on first visit and kept, so a running comparison survives the tabs; a
//                promoted column refreshes that draft's inbox row.
//   Preferences  note-expander-v2-prefs.jsx, with the Style row (Auto customize / Set by hand).
//   Your style   note-expander-v2-style.jsx (PR 8a-UI): auto customize's page. While a learned draft
//                waits for review, the inbox carries a banner to it until the physician sets it aside.
//   Earlier feedback  note-expander-v2-feedback.jsx (read-only).
// The model picker offers exactly what GET /v2/models serves; nothing here names a model id
// (the W-GATE-ALL sibling pin, tests/security/noteExpanderV2Page.test.js).

const ELATION_BASE = 'https://app.elationemr.com/patient/';

function startErrorMessage(e, copy) {
  const reason = e && e.body && e.body.reason;
  if (e && e.status === 422 && reason) return copyFor(copy, 'run', reason) || 'This draft cannot be expanded.';
  if (e && e.status === 409 && reason) return copyFor(copy, 'request', reason) || 'This draft cannot be expanded now.';
  if (e && e.status === 400) return 'That model is not available. Choose another.';
  return 'The expansion could not start. Try again.';
}

export function NoteExpanderV2Page({ copy }) {
  // 'inbox' | 'playground' | 'prefs' | 'style' | 'feedback'; ?ne= picks the first one (the in-chart
  // panel's "More settings" opens Preferences).
  const [tab, setTab] = useState(() => subTabFromSearch(typeof window === 'undefined' ? '' : window.location.search) || 'inbox');
  const [view, setView] = useState(null); // GET /v2/me/prefs
  const [style, setStyle] = useState(null); // GET /v2/me/style, or null when it could not be read
  const [asideDraft, setAsideDraft] = useState(null); // the draft whose banner was set aside this visit
  const [seenUpdate, setSeenUpdate] = useState(null); // the updated version whose notice was seen this visit
  const [models, setModels] = useState(null); // GET /v2/models
  const [setupError, setSetupError] = useState('');
  const [model, setModel] = useState('');
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeBusy, setNoticeBusy] = useState(false);
  const [playVisited, setPlayVisited] = useState(false);
  const [inboxRefresh, setInboxRefresh] = useState(null); // { draftId, n }: a draft whose row changed elsewhere
  useEffect(() => { if (tab === 'playground') setPlayVisited(true); }, [tab]);

  function applyView(v) {
    setView(v);
    setModel(v.model.alias);
    const saved = v.settings.defaultModel;
    let storage = null;
    try { storage = window.sessionStorage; } catch (_) { storage = null; }
    setNoticeOpen(!!v.model.fellBack && !fellBackSeen(storage, saved));
  }

  useEffect(() => {
    let live = true;
    // Your style never holds the page up: it is read beside the preferences, and a failure leaves
    // its tab and banner out.
    ccmApi.ne2Style().then((st) => { if (live) setStyle(st); }).catch(() => {});
    Promise.all([ccmApi.ne2Prefs(), ccmApi.ne2Models()])
      .then(([v, m]) => { if (!live) return; setModels(m); applyView(v); })
      .catch(() => { if (live) setSetupError('Could not load your Note Expander preferences. Reload the page to try again.'); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After a style change: the style, and the preferences it may have changed (an approved learned
  // style brings its options).
  function refreshStyle() {
    return Promise.all([ccmApi.ne2Style().then(setStyle), ccmApi.ne2Prefs().then(applyView)]);
  }

  let localStore = null;
  try { localStore = window.localStorage; } catch (_) { localStore = null; }
  // Kept by draft: a later learned draft asks again, even when storage is unavailable.
  const showBanner = !!style && bannerShows(style, localStore) && !(style.draft && asideDraft === style.draft.id);
  function setBannerAside() {
    const id = style && style.draft && style.draft.id;
    dismissBanner(localStore, id);
    setAsideDraft(id);
  }
  // An automatic update (8b-2) shows until Your style is opened or the notice is set aside.
  const update = style ? updateNotice(style, localStore) : null;
  const showUpdate = !!update && seenUpdate !== update.id;
  function setUpdateSeen() {
    if (!update) return;
    markUpdateSeen(localStore, update.id);
    setSeenUpdate(update.id);
  }
  const updateId = update ? update.id : null;
  useEffect(() => { // opening Your style is seeing the update
    if (tab === 'style' && updateId) setUpdateSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, updateId]);

  function dismissNotice() {
    let storage = null;
    try { storage = window.sessionStorage; } catch (_) { storage = null; }
    markFellBackSeen(storage, view && view.settings.defaultModel);
    setNoticeOpen(false);
  }

  async function usePracticeDefault() {
    if (!view) return;
    setNoticeBusy(true);
    try {
      const saved = view.settings.defaultModel;
      const next = await ccmApi.ne2SavePrefs(prefsBody({ ...view.settings, defaultModel: null }));
      let storage = null;
      try { storage = window.sessionStorage; } catch (_) { storage = null; }
      markFellBackSeen(storage, saved);
      applyView(next);
    } catch (_) {
      setSetupError('Your default model could not be changed. Try again from Preferences.');
    } finally {
      setNoticeBusy(false);
    }
  }

  const entries = (models && models.models) || [];
  const byAlias = useMemo(() => new Map(entries.map((m) => [m.alias, m])), [entries]);
  const savedEntry = view && view.settings.defaultModel ? byAlias.get(view.settings.defaultModel) : null;
  const usingEntry = view ? byAlias.get(view.model.alias) : null;

  return (
    <div className="ne2-page">
      <div className="ne-toolbar">
        <div className="ne-filter-group" role="tablist" aria-label="Note Expander">
          <button type="button" role="tab" aria-selected={tab === 'inbox'}
                  className={'filter-pill' + (tab === 'inbox' ? ' active' : '')}
                  onClick={() => setTab('inbox')}>Inbox</button>
          <button type="button" role="tab" aria-selected={tab === 'playground'}
                  className={'filter-pill' + (tab === 'playground' ? ' active' : '')}
                  onClick={() => setTab('playground')}>Playground</button>
          <button type="button" role="tab" aria-selected={tab === 'prefs'}
                  className={'filter-pill' + (tab === 'prefs' ? ' active' : '')}
                  onClick={() => setTab('prefs')}>Preferences</button>
          {style && (
            <button type="button" role="tab" aria-selected={tab === 'style'}
                    className={'filter-pill' + (tab === 'style' ? ' active' : '')}
                    onClick={() => setTab('style')}>Your style</button>
          )}
          <button type="button" role="tab" aria-selected={tab === 'feedback'}
                  className={'filter-pill' + (tab === 'feedback' ? ' active' : '')}
                  onClick={() => setTab('feedback')}>Earlier feedback</button>
        </div>
        {tab === 'inbox' && view && (
          <label className="ne-model-pick">Model
            <select value={model} onChange={(e) => setModel(e.target.value)} aria-label="Expansion model">
              {entries.map((m) => (
                <option key={m.alias} value={m.alias}>{modelOptionLabel(m, view.practiceDefaultModel)}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {setupError && <div className="ne-error" role="alert">{setupError}</div>}

      {noticeOpen && view && (
        <div className="ne2-notice" role="status">
          <p>
            Your default model{savedEntry ? ` (${savedEntry.display})` : ''} is not available right now, so
            expansions use {usingEntry ? usingEntry.display : 'the practice default'}. Your choice is kept.
          </p>
          <div className="ne2-notice-actions">
            <button type="button" className="btn sm" onClick={usePracticeDefault} disabled={noticeBusy}>
              {noticeBusy ? 'Saving…' : 'Use the practice default'}
            </button>
            <button type="button" className="btn sm ghost" onClick={dismissNotice} disabled={noticeBusy}>Dismiss</button>
          </div>
        </div>
      )}

      {tab === 'inbox' && showUpdate && (
        <div className="ne2-notice" role="status">
          <p>Your style was updated{update.date ? ` on ${update.date}` : ''}: {update.text}. Restore an earlier version any time.</p>
          <div className="ne2-notice-actions">
            <button type="button" className="btn sm" onClick={() => setTab('style')}>Review</button>
            <button type="button" className="btn sm ghost" onClick={setUpdateSeen}>OK</button>
          </div>
        </div>
      )}

      {tab === 'inbox' && showBanner && (
        <div className="ne2-notice" role="status">
          <p>Your style profile is ready to review: learned from your own notes, so expansions read like you wrote them.</p>
          <div className="ne2-notice-actions">
            <button type="button" className="btn sm" onClick={() => setTab('style')}>Review</button>
            <button type="button" className="btn sm ghost" onClick={setBannerAside}>Not now</button>
          </div>
        </div>
      )}

      {!view && !setupError && <div className="ne-empty">Loading…</div>}

      {/* The inbox stays mounted behind Preferences (as v1's does), so a run being polled and a
          note's switched mode survive a trip to the other tab. */}
      {view && (
        <div style={tab === 'inbox' ? undefined : { display: 'none' }} aria-hidden={tab === 'inbox' ? undefined : 'true'}>
          <V2Inbox copy={copy} defaultMode={view.settings.defaultMode} model={model} refresh={inboxRefresh} />
        </div>
      )}
      {view && playVisited && (
        <div style={tab === 'playground' ? undefined : { display: 'none' }} aria-hidden={tab === 'playground' ? undefined : 'true'}>
          <NoteExpanderV2Playground copy={copy} view={view} models={models} onSaved={applyView}
                                    onPromoted={(draftId) => setInboxRefresh({ draftId, n: Date.now() })} />
        </div>
      )}
      {view && tab === 'prefs' && (
        <NoteExpanderV2Prefs view={view} models={models} copy={copy} onSaved={applyView}
                             style={style} onStyleChange={refreshStyle} onOpenStyle={() => setTab('style')} />
      )}
      {view && style && tab === 'style' && (
        <NoteExpanderV2Style style={style} models={models} copy={copy} practiceDefaultModel={view.practiceDefaultModel}
                             onChanged={refreshStyle} onEditPrefs={() => setTab('prefs')} />
      )}
      {view && tab === 'feedback' && <NoteExpanderV2Feedback />}
    </div>
  );
}

function V2Inbox({ copy, defaultMode, model, refresh }) {
  const [scope, setScope] = useState('all'); // 'all' | 'mine'
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [nextOffset, setNextOffset] = useState(null);
  const [mineAvailable, setMineAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [runs, setRuns] = useState({}); // draftId → latest run row (+ { stale } when polling gave up)
  const [modeFor, setModeFor] = useState({}); // draftId → this note's mode, when switched
  const [starting, setStarting] = useState({}); // draftId → true while POST /runs is in flight
  const [startError, setStartError] = useState({}); // draftId → words
  const [selected, setSelected] = useState(null);
  const [contextByPatient, setContextByPatient] = useState({});
  const [reviewing, setReviewing] = useState(null); // { runId, draftId, patientName, patientElationId }
  const seq = useRef(0);
  const debounce = useRef(null);
  const pollers = useRef(new Map()); // draftId → { timer, attempt, since }
  const mounted = useRef(true);

  useEffect(() => () => {
    mounted.current = false;
    for (const p of pollers.current.values()) clearTimeout(p.timer);
    pollers.current.clear();
    clearTimeout(debounce.current);
  }, []);

  function rememberRuns(list) {
    if (!list || !list.length) return;
    setRuns((prev) => {
      const next = { ...prev };
      for (const r of list) next[String(r.draftId)] = r;
      return next;
    });
    for (const r of list) if (!isTerminal(r.status)) poll(String(r.draftId));
  }

  function poll(draftId) {
    if (pollers.current.has(draftId)) return;
    const state = { timer: null, attempt: 0, since: Date.now() };
    pollers.current.set(draftId, state);
    const tick = async () => {
      if (!mounted.current) return;
      if (Date.now() - state.since > POLL_BUDGET_MS) {
        pollers.current.delete(draftId);
        setRuns((prev) => ({ ...prev, [draftId]: { ...(prev[draftId] || {}), stale: true } }));
        return;
      }
      try {
        const r = await ccmApi.ne2LatestRuns([draftId]);
        const row = (r.runs || [])[0];
        if (!mounted.current) return;
        if (row) {
          setRuns((prev) => ({ ...prev, [draftId]: row }));
          if (isTerminal(row.status)) { pollers.current.delete(draftId); return; }
        }
      } catch (_) { /* a missed poll is retried on the next tick */ }
      state.attempt += 1;
      state.timer = setTimeout(tick, nextPollDelay(state.attempt));
    };
    state.timer = setTimeout(tick, nextPollDelay(0));
  }

  async function loadRunsFor(drafts) {
    const ids = drafts.map((d) => String(d.draftId));
    for (const part of chunk(ids)) {
      try {
        const r = await ccmApi.ne2LatestRuns(part);
        if (mounted.current) rememberRuns(r.runs || []);
      } catch (_) { /* the drafts still list; their run state shows on the next load */ }
    }
  }

  async function load(nextScope, query, offset = 0) {
    const mySeq = ++seq.current;
    setBusy(true); setError('');
    try {
      const r = await ccmApi.noteExpanderInbox({ scope: nextScope, q: query, offset });
      if (mySeq !== seq.current) return;
      const drafts = r.drafts || [];
      setMineAvailable(!!r.mineAvailable);
      setRows((prev) => (offset ? prev.concat(drafts) : drafts));
      setNextOffset(r.nextOffset != null ? r.nextOffset : null);
      if (drafts.length) loadRunsFor(drafts);
    } catch (e) {
      if (mySeq !== seq.current) return;
      setError('Could not load the inbox.');
    } finally {
      if (mySeq === seq.current) setBusy(false);
    }
  }

  useEffect(() => { load(scope, q.trim(), 0); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [scope]);

  // A draft whose run changed elsewhere (a Playground column promoted into it): one read of its row.
  useEffect(() => {
    if (!refresh || !refresh.draftId) return;
    ccmApi.ne2LatestRuns([refresh.draftId]).then((r) => { if (mounted.current) rememberRuns(r.runs || []); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  function onSearch(v) {
    setQ(v); clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(scope, v.trim(), 0), 250);
  }

  const merged = useMemo(() => mergeInbox(rows, Object.values(runs)), [rows, runs]);
  const groups = useMemo(() => groupByPatient(merged), [merged]);

  useEffect(() => {
    if (!groups.length) { if (selected != null) setSelected(null); return; }
    if (selected != null && groups.some((g) => String(g.elationId) === String(selected))) return;
    setSelected((groups.find((g) => g.toExpand > 0) || groups[0]).elationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  useEffect(() => {
    const eid = selected;
    if (eid == null || contextByPatient[eid]) return;
    setContextByPatient((prev) => ({ ...prev, [eid]: { loading: true } }));
    ccmApi.noteExpanderPatientContext(eid).then((r) => {
      if (!mounted.current) return;
      setContextByPatient((prev) => ({ ...prev, [eid]: { loading: false, problems: r.problems || [], medications: r.medications || [], allergies: r.allergies || [] } }));
    }).catch(() => {
      if (!mounted.current) return;
      setContextByPatient((prev) => ({ ...prev, [eid]: { loading: false, error: true } }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  async function expand(row) {
    const draftId = String(row.draftId);
    const mode = modeFor[draftId] || defaultMode;
    setStarting((prev) => ({ ...prev, [draftId]: true }));
    setStartError((prev) => ({ ...prev, [draftId]: '' }));
    try {
      const r = await ccmApi.ne2StartRun({ draftId, mode, model, clientRequestId: newClientRequestId() });
      const run = r.run;
      rememberRuns([{ draftId, runId: run.id, status: run.status, statusReason: run.statusReason, mode: run.mode, source: run.source, revisionId: null, revisionStatus: null, openHighlights: null }]);
    } catch (e) {
      if (e && e.status === 409 && e.body && e.body.reason === 'run_in_progress') {
        rememberRuns([{ draftId, runId: e.body.liveRunId || null, status: 'running', mode }]);
      } else {
        setStartError((prev) => ({ ...prev, [draftId]: startErrorMessage(e, copy) }));
      }
    } finally {
      setStarting((prev) => ({ ...prev, [draftId]: false }));
    }
  }

  const group = groups.find((g) => String(g.elationId) === String(selected)) || null;
  const ctx = group ? contextByPatient[group.elationId] : null;

  // Closing the review refreshes that draft's row: its touches, a write or a discard changed it,
  // and the inbox polls only runs still in flight.
  async function closeReview() {
    const draftId = reviewing && reviewing.draftId;
    setReviewing(null);
    if (!draftId) return;
    try {
      const r = await ccmApi.ne2LatestRuns([draftId]);
      if (mounted.current) rememberRuns(r.runs || []);
    } catch (_) { /* the row refreshes on the next load */ }
  }

  if (reviewing) {
    return (
      <NoteExpanderV2Review runId={reviewing.runId} copy={copy} patientName={reviewing.patientName}
                            patientElationId={reviewing.patientElationId} onClose={closeReview} />
    );
  }

  return (
    <>
      <div className="ne-toolbar ne2-subbar">
        <div className="ne-filter-group" role="tablist" aria-label="Inbox scope">
          <button type="button" role="tab" aria-selected={scope === 'all'}
                  className={'filter-pill' + (scope === 'all' ? ' active' : '')}
                  onClick={() => setScope('all')}>All</button>
          {mineAvailable && (
            <button type="button" role="tab" aria-selected={scope === 'mine'}
                    className={'filter-pill' + (scope === 'mine' ? ' active' : '')}
                    onClick={() => setScope('mine')}>Mine</button>
          )}
        </div>
        <div className="ne-search">
          <input value={q} placeholder="Filter by patient name…" onChange={(e) => onSearch(e.target.value)}
                 aria-label="Filter inbox by patient" />
        </div>
      </div>

      <div className="ne-split">
        <aside className="ne-rail">
          <div className="ne-rail-label">Patients · {groups.length}</div>
          {error && <div className="ne-error" role="alert">{error}</div>}
          {busy && !groups.length && <div className="ne-empty">Loading…</div>}
          {!busy && !error && !groups.length && (
            <div className="ne-empty">No unsigned drafts{q.trim() ? ' match that name.' : '.'}</div>
          )}
          {groups.map((g) => {
            const active = String(g.elationId) === String(selected);
            const ready = g.drafts.filter((d) => rowState(d.run, copy).kind === 'ready').length;
            return (
              <button key={String(g.elationId)} type="button"
                      className={'ne-rail-item' + (active ? ' is-active' : '')}
                      aria-current={active ? 'true' : undefined}
                      onClick={() => setSelected(g.elationId)}>
                <div className="ne-rail-name">{g.name}</div>
                <div className="ne-rail-counts">
                  {g.toExpand > 0 && <span className="ne-count-chip">{g.toExpand} to expand</span>}
                  {ready > 0 && <span className="ne-count-chip ne2-chip-ready">{ready} to review</span>}
                </div>
              </button>
            );
          })}
          {nextOffset != null && (
            <button type="button" className="ne-load-more" onClick={() => load(scope, q.trim(), nextOffset)} disabled={busy}>
              {busy ? 'Loading…' : 'Load more'}
            </button>
          )}
        </aside>

        <section className="ne-detail">
          {!group && <div className="ne-empty ne-detail-empty">Select a patient to see their drafts.</div>}
          {group && (
            <>
              <header className="ne-patient-header">
                <h2 className="ne-patient-name">{group.name}</h2>
                <div className="ne-ctx">
                  <div className="ne-ctx-label">Chart context</div>
                  <div className="ne-ctx-chips">
                    {ctx && ctx.loading && <span className="ne-ctx-loading">Loading chart…</span>}
                    {ctx && !ctx.loading && ctx.error && <span className="ne-ctx-loading">Chart context unavailable.</span>}
                    {ctx && !ctx.loading && !ctx.error && (() => {
                      const chips = [
                        ...(ctx.problems || []).map((p, i) => <span key={'p' + i} className="ne-ctx-chip">{p}</span>),
                        ...(ctx.medications || []).map((m, i) => <span key={'m' + i} className="ne-ctx-chip">{m}</span>),
                        ...(ctx.allergies || []).map((a, i) => <span key={'a' + i} className="ne-ctx-chip">Allergy: {a}</span>),
                      ];
                      return chips.length ? chips : <span className="ne-ctx-loading">No documented chart context.</span>;
                    })()}
                  </div>
                </div>
              </header>
              <div className="ne-cards">
                {group.drafts.map((row) => (
                  <DraftCard key={String(row.draftId)} row={row} copy={copy}
                             mode={modeFor[String(row.draftId)] || defaultMode} defaultMode={defaultMode}
                             onMode={(m) => setModeFor((prev) => ({ ...prev, [String(row.draftId)]: m }))}
                             starting={!!starting[String(row.draftId)]}
                             error={startError[String(row.draftId)] || ''}
                             onExpand={() => expand(row)}
                             onReview={row.run && row.run.runId ? () => setReviewing({
                               runId: row.run.runId, draftId: String(row.draftId),
                               patientName: row.patient && row.patient.name, patientElationId: row.patient && row.patient.elationId,
                             }) : null} />
                ))}
              </div>
            </>
          )}
        </section>
      </div>
    </>
  );
}

function DraftCard({ row, copy, mode, defaultMode, onMode, starting, error, onExpand, onReview }) {
  const c = row.classification || {};
  const canExpand = !!c.expandable || c.kind === 'expanded';
  const state = rowState(row.run, copy);
  const running = state.kind === 'running' && !(row.run && row.run.stale);
  const patientId = row.patient && row.patient.elationId;
  const [full, setFull] = useState(false);
  const long = !!row.preview && row.preview.length > 280;
  return (
    <article className="ne-card ne2-card">
      <div className="ne-card-head">
        {row.provider && <span className="ne-card-provider">{row.provider}</span>}
        <span className="ne-card-spacer" />
        {row.date && <span className="ne-card-date">{String(row.date).slice(0, 10)}</span>}
      </div>
      {row.preview && <div className={'ne-card-body ne2-card-body' + (full || !long ? ' is-full' : '')}>{row.preview}</div>}
      {long && (
        <button type="button" className="ne2-more" aria-expanded={full} onClick={() => setFull((f) => !f)}>
          {full ? 'Show less' : 'Show all'}
        </button>
      )}

      {state.kind !== 'none' && (
        <div className={'ne2-state ne2-state-' + state.kind} role="status">
          {row.run && row.run.stale ? 'Still expanding. Reload the page to check again.' : state.label}
        </div>
      )}
      {error && <div className="ne2-state ne2-state-failed" role="alert">{error}</div>}

      {canExpand && (
        <div className="ne-card-actions ne2-actions">
          <div className="ne2-seg" role="radiogroup" aria-label="Mode for this note">
            {['grounded', 'maximal'].map((m) => (
              <button key={m} type="button" role="radio" aria-checked={mode === m}
                      className={'ne2-seg-btn' + (mode === m ? ' is-on' : '')}
                      disabled={running || starting} onClick={() => onMode(m)}>{modeLabel(m)}</button>
            ))}
          </div>
          {onReview && (state.kind === 'ready' || state.kind === 'done') && (
            <button type="button" className="btn accent" onClick={onReview}>{state.kind === 'ready' ? 'Review' : 'View'}</button>
          )}
          <button type="button" className={'btn' + (onReview && (state.kind === 'ready' || state.kind === 'done') ? '' : ' accent')}
                  onClick={onExpand} disabled={running || starting}>
            {starting ? 'Starting…' : running ? 'Expanding…' : state.kind === 'none' ? (c.kind === 'expanded' ? 'Expand anyway' : 'Expand') : 'Expand again'}
          </button>
          {patientId != null && (
            <a className="btn ghost sm" href={ELATION_BASE + encodeURIComponent(patientId)} target="_blank" rel="noopener noreferrer">
              Open in Elation
            </a>
          )}
        </div>
      )}
      {canExpand && (
        <div className="ne2-hint">
          Your default: {modeLabel(defaultMode)}{mode !== defaultMode ? ` · ${modeLabel(mode)} for this note only` : ' · switch for this note only'}
        </div>
      )}
    </article>
  );
}
